import { useState, useEffect, useCallback } from 'react';
import { useHorseStorage } from './useHorseStorage';
import { calculateBondingDecay, getTodayDateString } from '../../../utils/horseHelpers';
import { DEFAULT_HORSE_FORM } from '../../../constants/horseConstants';

/**
 * Custom hook for managing horse data (API + localStorage)
 */
export const useHorseData = (api) => {
  const [mustangs, setMustangs] = useState([]);
  const [loading, setLoading] = useState(true);
  const { getLocalHorses, saveLocalHorse, updateLocalHorse, deleteLocalHorse } = useHorseStorage();

  // Fetch horses from API and localStorage
  const fetchMustangs = useCallback(async () => {
    try {
      const response = await api.get('/user-mustangs');
      const apiHorses = response.data || [];
      
      // Apply bonding decay to API horses
      const apiHorsesWithDecay = apiHorses.map(horse => ({
        ...horse,
        bonding_level: calculateBondingDecay(
          horse.bonding_level || 0,
          horse.last_care_date
        )
      }));
      
      // Get horses from localStorage
      const localHorses = getLocalHorses();
      
      // Apply bonding decay to local horses
      const localHorsesWithDecay = localHorses.map(horse => ({
        ...horse,
        bonding_level: calculateBondingDecay(
          horse.bonding_level || 0,
          horse.last_care_date
        )
      }));
      
      // Combine and set
      setMustangs([...apiHorsesWithDecay, ...localHorsesWithDecay]);
    } catch (error) {
      console.error('Error fetching mustangs:', error);
      // If API fails, just load from localStorage
      const localHorses = getLocalHorses();
      const localHorsesWithDecay = localHorses.map(horse => ({
        ...horse,
        bonding_level: calculateBondingDecay(
          horse.bonding_level || 0,
          horse.last_care_date
        )
      }));
      setMustangs(localHorsesWithDecay);
    }
    setLoading(false);
  }, [api, getLocalHorses]);

  // Add a new horse
  const addHorse = useCallback(async () => {
    try {
      console.log('[ADD_HORSE] Creating new horse with default data');
      
      // Use the complete default form structure
      const defaultHorse = {
        ...DEFAULT_HORSE_FORM,
        name: 'New Horse',
        age: 5,
        gender: 'Mare',
        color: 'Bay',
        hma: 'Unknown',
        height: '14.2 hands',
        disposition: 'Gentle',
        is_real_adoption: true
      };
      
      const response = await api.post('/user-mustangs/add-real', defaultHorse);
      console.log('[ADD_HORSE] Horse created:', response.data);
      
      // Refresh the list to show new horse
      await fetchMustangs();
      
      return { success: true, message: 'New horse added! 🐴' };
    } catch (error) {
      console.error('[ADD_HORSE] Error creating horse:', error);
      console.error('[ADD_HORSE] Error response:', error.response);
      
      // Extract error message properly handling validation errors
      let errorMessage = 'Unknown error occurred';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        
        // If detail is an array (validation errors), extract messages
        if (Array.isArray(detail)) {
          errorMessage = detail.map(err => err.msg || err.message || 'Validation error').join(', ');
        } else if (typeof detail === 'string') {
          // If detail is a string, use it directly
          errorMessage = detail;
        }
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      console.error('[ADD_HORSE] Extracted error message:', errorMessage);
      
      return { 
        success: false, 
        message: `Failed to create horse: ${errorMessage}` 
      };
    }
  }, [api, fetchMustangs]);

  // Update a horse
  const updateHorse = useCallback(async (horseId, updates) => {
    try {
      if (horseId.startsWith('local_')) {
        // Update local horse
        updateLocalHorse(horseId, updates);
        
        // Update in state
        setMustangs(prev => prev.map(m => 
          m.id === horseId ? { ...m, ...updates } : m
        ));
      } else {
        // Update API horse
        await api.put(`/user-mustangs/${horseId}`, updates);
        
        // Update in state
        setMustangs(prev => prev.map(m => 
          m.id === horseId ? { ...m, ...updates } : m
        ));
      }
      
      return { success: true };
    } catch (error) {
      console.error('Error updating horse:', error);
      // Revert on error
      await fetchMustangs();
      return { success: false, error };
    }
  }, [api, updateLocalHorse, fetchMustangs]);

  // Delete a horse
  const deleteHorse = useCallback(async (horseId) => {
    try {
      if (horseId.startsWith('local_')) {
        deleteLocalHorse(horseId);
      } else {
        await api.delete(`/user-mustangs/${horseId}`);
      }
      
      // Remove from state
      setMustangs(prev => prev.filter(m => m.id !== horseId));
      
      return { success: true };
    } catch (error) {
      console.error('Error deleting horse:', error);
      return { success: false, error };
    }
  }, [api, deleteLocalHorse]);

  // Optimistically update a horse (UI first, then API)
  const optimisticUpdate = useCallback((horseId, updates) => {
    setMustangs(prev => prev.map(m => 
      m.id === horseId ? { ...m, ...updates } : m
    ));
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchMustangs();
  }, [fetchMustangs]);

  return {
    mustangs,
    loading,
    fetchMustangs,
    addHorse,
    updateHorse,
    deleteHorse,
    optimisticUpdate,
    setMustangs
  };
};
